/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

/**
 * 首頁組件
 *
 * 功能：
 * - 顯示搜尋框，提交後導向搜尋頁面
 * - 顯示專案簡介
 * - 顯示搜尋語法說明
 *
 * Props: 無
 */
const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // 導向搜尋頁面，帶上搜尋關鍵字
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* 標題區域 */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 600,
            color: 'primary.main',
            mb: 2,
          }}
        >
          佛法搜尋資料庫
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
          支援繁體中文、簡體中文、英文多語言搜尋
        </Typography>

        {/* 搜尋框 */}
        <Paper
          component="form"
          onSubmit={handleSearch}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'stretch',
            boxShadow: 3,
            borderRadius: 2,
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <TextField
              fullWidth
              placeholder="請輸入搜尋關鍵字..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: '1.1rem',
                  px: 1,
                },
              }}
            />
          </Box>
          <Button
            type="submit"
            variant="contained"
            sx={{
              ml: 2,
              px: 3,
              py: 2,
              minWidth: 70,
              borderRadius: 1,
              fontSize: '1.1rem',
              fontWeight: 500,
              letterSpacing: '0.5em',
              writingMode: 'vertical-rl',
              textOrientation: 'upright',
            }}
          >
            搜尋
          </Button>
        </Paper>

        {/* 搜尋提示 */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          提示：系統會自動進行繁簡轉換，無論輸入繁體或簡體都能搜尋到結果
        </Typography>
      </Box>

      {/* 搜尋語法說明 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          搜尋語法
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Chip label="基本搜尋" color="primary" size="small" sx={{ mr: 1 }} />
            <Typography variant="body1" component="span">
              直接輸入關鍵字，例如：<strong>佛法</strong>
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Chip label="AND 邏輯" color="primary" size="small" sx={{ mr: 1 }} />
            <Typography variant="body1" component="span">
              使用 <strong>+</strong> 連接，例如：<strong>佛法 + 正見</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              （必須同時包含兩個關鍵字）
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Chip label="OR 邏輯" color="primary" size="small" sx={{ mr: 1 }} />
            <Typography variant="body1" component="span">
              使用 <strong>/</strong> 連接，例如：<strong>佛法 / 禪修</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              （包含任一關鍵字即可）
            </Typography>
          </Box>

          <Box>
            <Chip label="混合邏輯" color="primary" size="small" sx={{ mr: 1 }} />
            <Typography variant="body1" component="span">
              組合使用，例如：<strong>佛法 + 正見 / 邪見</strong>
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* 使用說明 */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          快速開始
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" paragraph>
            如果您是第一次使用本站，建議依照以下步驟：
          </Typography>
          <Box component="ol" sx={{ pl: 3 }}>
            <li>
              <Typography variant="body1" paragraph>
                在上方搜尋框輸入您想了解的佛法主題
              </Typography>
            </li>
            <li>
              <Typography variant="body1" paragraph>
                瀏覽搜尋結果，點擊感興趣的文獻標題查看詳情
              </Typography>
            </li>
            <li>
              <Typography variant="body1">使用標籤篩選功能，縮小搜尋範圍</Typography>
            </li>
          </Box>
        </Paper>
      </Box>

      {/* 資料庫收錄 */}
      <Box>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          資料庫收錄
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1">
            弘法視頻文字檔、電子報、講堂出版的藏密相關書籍、會外有關藏密資料......等
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default HomePage;
